export type AppType = "Taap" | "STPM" | "IFC";
export type AppScheme =
  | "taap.dev"
  | "taap.stg"
  | "taap"
  | "taap.stpm.dev"
  | "taap.stpm.stg"
  | "taap.stpm"
  | "stpm.pnpt.space"
  | "dev.ifc.pnpt.space"
  | "stg.ifc.pnpt.space"
  | "ifc.pnpt.space";

export interface HostInfo {
  hostname: string;
  appType: AppType;
  appScheme: AppScheme;
}

const hostInfos: HostInfo[] = [
  {
    hostname: "localhost",
    appType: "Taap",
    appScheme: "taap.dev",
  },
  {
    hostname: "dev-court.pnpt.net",
    appType: "Taap",
    appScheme: "taap.dev",
  },
  {
    hostname: "stg-court.pnpt.net",
    appType: "Taap",
    appScheme: "taap.stg",
  },
  {
    hostname: "taapspace.kr",
    appType: "Taap",
    appScheme: "taap",
  },
  {
    hostname: "taap.pnpt.space",
    appType: "Taap",
    appScheme: "taap",
  },
  {
    hostname: "dev-court-stpm.pnpt.net",
    appType: "STPM",
    appScheme: "taap.stpm.dev",
  },
  {
    hostname: "stg-court-stpm.pnpt.net",
    appType: "STPM",
    appScheme: "taap.stpm.stg",
  },
  {
    hostname: "prod-court-stpm.pnpt.net",
    appType: "STPM",
    appScheme: "taap.stpm",
  },
  {
    hostname: "stpm.pnpt.space",
    appType: "STPM",
    appScheme: "stpm.pnpt.space",
  },
  {
    hostname: "dev-court-ifc.pnpt.net",
    appType: "IFC",
    appScheme: "dev.ifc.pnpt.space",
  },
  {
    hostname: "stg-court-ifc.pnpt.net",
    appType: "IFC",
    appScheme: "stg.ifc.pnpt.space",
  },
  {
    hostname: "ifc.pnpt.space",
    appType: "IFC",
    appScheme: "ifc.pnpt.space",
  },
];

const localAppTypeSchemeMap: Record<AppType, AppScheme> = {
  Taap: "taap.dev",
  STPM: "taap.stpm.dev",
  IFC: "dev.ifc.pnpt.space",
};

const getHostInfo = (): HostInfo | undefined => {
  const { hostname } = window.location;

  // 로컬 개발 시 REACT_APP_LOCAL_APP_TYPE 으로 appType 오버라이드
  if (hostname === "localhost" && process.env.REACT_APP_LOCAL_APP_TYPE) {
    const appType = process.env.REACT_APP_LOCAL_APP_TYPE as AppType;
    return {
      hostname,
      appType,
      appScheme: localAppTypeSchemeMap[appType],
    };
  }

  const hostInfo = hostInfos.find((hostInfo) => {
    return hostInfo.hostname === hostname;
  });

  return hostInfo;
};

const getAppInfo = () => {
  const { appType, appScheme, hostname } = getHostInfo() || {};

  return {
    hostname,
    appType,
    appScheme,
  };
};

export const getBaseUrl = (): string => {
  const { appType } = getAppInfo();
  switch (appType) {
    case "IFC":
      return process.env.REACT_APP_IFC_BASEURL || "";
    case "STPM":
      return process.env.REACT_APP_STPM_BASEURL || "";
    case "Taap":
    default:
      return process.env.REACT_APP_COURT_BASEURL || process.env.REACT_APP_PPOINT_BASEURL || "";
  }
};

export const getAuthConfig = () => {
  const { appType } = getAppInfo();
  const jwtIssuer = process.env.REACT_APP_JWT_ISSUER!;
  const issuer = `${getBaseUrl()}/api/court-auth`;

  switch (appType) {
    case "IFC":
      return { issuer, jwtIssuer, clientId: process.env.REACT_APP_IFC_AUTH_CLIENT_ID!, redirectUri: process.env.REACT_APP_IFC_AUTH_REDIRECT_URI! };
    case "STPM":
      return { issuer, jwtIssuer, clientId: process.env.REACT_APP_STPM_AUTH_CLIENT_ID!, redirectUri: process.env.REACT_APP_STPM_AUTH_REDIRECT_URI! };
    case "Taap":
    default:
      return { issuer, jwtIssuer, clientId: process.env.REACT_APP_COURT_AUTH_CLIENT_ID!, redirectUri: process.env.REACT_APP_COURT_AUTH_REDIRECT_URI! };
  }
};

export const getAppTitle = (): string => {
  const { appType } = getAppInfo();
  switch (appType) {
    case "STPM":
      return "SmartThings Pro Mobile";
    case "IFC":
      return "IFC Seoul";
    case "Taap":
    default:
      return "TaapSpace";
  }
};

/**
 * 도메인별 마이페이지 타이틀을 반환한다.
 * (예: "마이페이지 | SmartThings Pro Mobile")
 */
export const getMypageTitle = (): string => {
  return `마이페이지 | ${getAppTitle()}`;
};

export const getFaviconPath = (): string => {
  const { appType } = getAppInfo();
  const s3Base = process.env.REACT_APP_S3_CONTENT_BASEURL || "";
  switch (appType) {
    case "STPM":
      return `${s3Base}stpm/favicon.ico`;
    case "IFC":
      return `${s3Base}ifc/favicon.ico`;
    case "Taap":
    default:
      return `${s3Base}court/favicon.ico`;
  }
};

export const getOgTitle = (): string => {
  const { appType } = getAppInfo();
  switch (appType) {
    case "STPM":
      return "ST Pro Mobile";
    case "IFC":
      return "IFC Seoul";
    case "Taap":
    default:
      return "TaapSpace";
  }
};

export const getOgImagePath = (): string => {
  const { appType } = getAppInfo();
  const s3Base = process.env.REACT_APP_S3_CONTENT_BASEURL || "";
  switch (appType) {
    case "STPM":
      return `${s3Base}court/logo_stpm_og.png`;
    case "IFC":
      return `${s3Base}court/logo_ifc_og.png`;
    case "Taap":
    default:
      return `${s3Base}court/logo_taapspace_og.png`;
  }
};

export default getAppInfo;
